import { z } from 'zod';
import { GAME_IDS, type GameId } from './common';
import type { ApiError } from './errors';
import type { ProgressEvent } from './progression';

/* ============================== Friends ============================== */

/** Other players are addressed by their public number (never by internal ids / emails). */
export const zPublicId = z.number().int().positive().max(2_147_483_647);
export const zFriendTarget = z.object({ publicId: zPublicId }).strict();
export const zUserSearch = z.object({ q: z.string().trim().min(2).max(24) });

/** playing = connected and in a game room; online = connected (lobby, profile…); offline = no socket */
export type PresenceStatus = 'playing' | 'online' | 'offline';

export interface FriendView {
  publicId: number;
  nickname: string;
  avatar: string;
  level: number;
  status: PresenceStatus;
  /** game room the friend is in right now ("Join table") */
  game: GameId | null;
  lastSeenAt: string | null;
  /** best payout multiple today (settled rounds), null if none */
  bestToday: number | null;
}

export interface FriendRequestView {
  publicId: number;
  nickname: string;
  avatar: string;
  level: number;
  createdAt: string;
}

/** GET /api/friends */
export interface FriendsState {
  friends: FriendView[];
  incoming: FriendRequestView[];
  outgoing: FriendRequestView[];
}

/**
 * Solo games whose round lives between requests (one unfinished round per player): «return to the game» on the menu.
 */
export const RESUMABLE_GAMES = ['carp', 'firecrackers', 'mahjong', 'zhajinhua'] as const satisfies readonly GameId[];
export type ResumableGame = (typeof RESUMABLE_GAMES)[number];

/** a registered tourney «starts soon» this long before its start (menu badge) */
export const TOURNEY_SOON_MS = 15 * 60_000;
/** an unseen tourney prize stays on the menu badge this long after the tourney finished */
export const TOURNEY_PRIZE_ALERT_MS = 14 * 24 * 3600_000;

/**
 * GET /api/me/badges and the `badges` socket event: what on the menu needs the player's attention.
 * - «Friends»: friendRequests — incoming pending requests;
 * - «Messages»: unreadChats — partners with unread messages (unreadMessages — unread messages in total);
 * - «Tournaments»: tourneys — table tourneys that need me: seated at a new table of a running stage, a tourney I
 *   entered starts within TOURNEY_SOON_MS, a prize I have not seen yet; each clears when I open the tourney or its
 *   table (POST /api/tourneys/:id/seen);
 * - «Games»: dailyBonus — the daily bonus can be claimed now (a dot); quest — the daily quest is done, its reward is
 *   waiting (a dot); resume — solo games with an unfinished round;
 * - «Profile»: achievements — awards (achievements and moments) the player has not seen yet; cleared when the
 *   achievements section of the profile is opened (POST /api/me/achievements/seen).
 * The fields after unreadChats are optional: an older server leaves them out.
 */
export interface Badges {
  friendRequests: number;
  unreadMessages: number;
  unreadChats: number;
  tourneys?: number;
  dailyBonus?: boolean;
  /** the daily quest is complete and not claimed yet */
  quest?: boolean;
  resume?: ResumableGame[];
  achievements?: number;
}

export type FriendRelation = 'self' | 'none' | 'friend' | 'outgoing' | 'incoming';

/** GET /api/friends/search?q= */
export interface UserSearchResult {
  publicId: number;
  nickname: string;
  avatar: string;
  level: number;
  relation: FriendRelation;
}

export const ACTIVITY_KINDS = ['big_win', 'pot_win', 'level_up', 'tournament_join', 'tournament_prize', 'tourney_join', 'tourney_prize'] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

/** GET /api/friends/activity → { items } */
export interface ActivityItem {
  id: string;
  kind: ActivityKind;
  nickname: string;
  at: string;
  game: GameId | null;
  amount: number | null;
  multiplier: number | null;
  level: number | null;
  tournament: string | null;
  place: number | null;
}

/**
 * Invite link: /:lang/i/<code>. Both players get `bonus` once, when the invited player's settled stakes add up to
 * `INVITE_TURNOVER` tokens (owner decision №6, 30.09). No bonus (friendship still made) when the invited account was
 * registered from the inviter's network (same IPv4 / IPv6 /64) or device, or when the inviter already has
 * `INVITE_DAILY_CAP` bonus-eligible invitees registered this UTC day.
 */
export const INVITE_BONUS = 500;
export const INVITE_TURNOVER = 1000;
export const INVITE_DAILY_CAP = 8;
export const INVITE_DENIALS = ['same_ip', 'same_device', 'daily_cap'] as const;
export type InviteDenial = (typeof INVITE_DENIALS)[number];

/**
 * Device signals sent with the sign-up (`device` in the register body), used only to deny the invite bonus between
 * accounts of one device. `id` — random per-browser id (localStorage); `fp` — UA-independent traits (languages,
 * screen, time zone, cores…) joined as text. The server stores only hashes (`fp` hashed together with User-Agent).
 */
export const zInviteDevice = z
  .object({
    id: z.string().regex(/^[A-Za-z0-9_-]{16,64}$/),
    fp: z.string().min(1).max(512),
  })
  .strict();
export type InviteDevice = z.infer<typeof zInviteDevice>;
export const INVITE_CODE_RE = /^[A-Z0-9]{6,12}$/;
export const INVITE_COOKIE = 'tg_invite';

/** GET /api/friends/invite */
export interface InviteInfo {
  code: string;
  bonus: number;
  /** invited player's total stakes that unlock the bonus */
  turnover: number;
  /** bonus-eligible invites per UTC day */
  dailyCap: number;
  /** players registered with the link */
  invited: number;
  /** of them: bonus already paid */
  rewarded: number;
}

/* ============================== Tournaments ============================== */

/**
 * best_multiplier  — highest single payout multiple (payout / stake)
 * total_multiplier — sum of payout multiples of winning rounds (payout > stake)
 * total_won        — sum of net winnings (payout − stake) of winning rounds, tokens
 * Scores are integers: multiples × 100, tokens as is.
 */
export const TOURNAMENT_METRICS = ['best_multiplier', 'total_multiplier', 'total_won'] as const;
export type TournamentMetric = (typeof TOURNAMENT_METRICS)[number];
export type TournamentStatus = 'scheduled' | 'live' | 'finished';

export function isMultiplierMetric(m: TournamentMetric): boolean {
  return m !== 'total_won';
}

export interface TournamentSummary {
  id: string;
  title: string;
  games: GameId[];
  metric: TournamentMetric;
  status: TournamentStatus;
  startsAt: string;
  endsAt: string;
  prizePool: number;
  /** prize per place: prizes[0] = 1st */
  prizes: number[];
  minBet: number;
  players: number;
  /** the current user has an entry */
  joined: boolean;
}

export interface LeaderRow {
  rank: number;
  publicId: number;
  nickname: string;
  avatar: string;
  score: number;
  rounds: number;
  /** prize for this place (final for finished tournaments, projected for live ones) */
  prize: number | null;
  /** places moved since the last snapshot (+ = up), null = new / unknown */
  move: number | null;
  me: boolean;
}

/** GET /api/tournaments/:id */
export interface TournamentDetails {
  tournament: TournamentSummary;
  top: LeaderRow[];
  me: LeaderRow | null;
  /** score of the 10th place (the "You need 5.40× to reach the top 10" hint), null if < 10 players */
  top10Score: number | null;
}

const zIso = z.string().datetime({ offset: true });

/** POST /api/admin/tournaments */
export const zTournamentCreate = z
  .object({
    title: z.string().trim().min(3).max(60),
    games: z.array(z.enum(GAME_IDS)).min(1).max(GAME_IDS.length),
    metric: z.enum(TOURNAMENT_METRICS),
    startsAt: zIso,
    endsAt: zIso,
    prizes: z.array(z.number().int().min(1).max(10_000_000)).min(1).max(50),
    minBet: z.number().int().min(1).max(1_000_000).default(1),
    comment: z.string().trim().min(3).max(500),
  })
  .strict()
  .refine((t) => Date.parse(t.endsAt) > Date.parse(t.startsAt), { path: ['endsAt'], message: 'endsAt must be after startsAt' });
export type TournamentCreateInput = z.input<typeof zTournamentCreate>;

/* ============================== Socket ============================== */

export interface SocialServerToClient {
  'progress:update': (e: ProgressEvent) => void;
  /** a friend's presence changed */
  'friends:presence': (e: { publicId: number; status: PresenceStatus; game: GameId | null }) => void;
  /** friend list / requests changed — refetch GET /api/friends */
  'friends:changed': () => void;
  /** menu counters changed (also sent once to a socket right after it connects) */
  badges: (b: Badges) => void;
  /** someone sent you a friend request (toast «New friend request: {nickname}») */
  'friends:request': (e: { publicId: number; nickname: string }) => void;
  /** a friend invites you to play */
  'friends:invite': (e: { publicId: number; nickname: string; game: GameId | null }) => void;
  /** leaderboard changed — refetch (throttled on the server) */
  'tournament:update': (e: { id: string }) => void;
  /** tournament finished: your final place (prize credited via the ledger) */
  'tournament:result': (e: { id: string; title: string; place: number; prize: number }) => void;
}

export interface SocialClientToServer {
  'tournament:watch': (id: string, ack: (r: { ok: true } | ApiError) => void) => void;
  'tournament:unwatch': (id: string) => void;
}
